"use client"

import "./HomePage.css"
import { Smartphone, Shield, Heart, Star, Users, Sparkles, Camera, Search, Zap } from "lucide-react"
import { PublicHeader, PublicFooter, usePublicLocale } from "../components/PublicLayout"

const HomePage = () => {
  const { copy: { home: text } } = usePublicLocale()
  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-orange-50">
      <PublicHeader home />

      <main>
      {/* Hero Section */}
      <section className="container public-hero mx-auto px-4 py-12 md:py-16">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div className="space-y-8 order-1 lg:order-1">
            <div className="space-y-6">
              <div
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium shadow-sm"
                style={{ backgroundColor: "#fff7ed", color: "#ff9f43" }}
              >
                <Sparkles className="w-4 h-4" /> {text.database}
              </div>
              <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold leading-tight text-gray-800">
                <span style={{ color: "#ff9f43" }}>{text.heroStart}</span>{" "}
                <span className="bg-gradient-to-r from-pink-500 to-purple-600 bg-clip-text text-transparent">
                  {text.heroAccent}
                </span>{" "}
                {text.heroEnd}
              </h1>
              <p className="text-xl text-gray-600 leading-relaxed">
                {text.description}
              </p>
            </div>

            <div className="public-store-actions flex flex-col sm:flex-row gap-4">
              <a
                href="https://apps.apple.com/app/id6744443804"
                className="inline-flex items-center justify-center gap-3 text-white px-8 py-4 text-lg font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all no-underline"
                style={{ backgroundColor: "#ff9f43" }}
              >
                <Smartphone className="w-6 h-6" />
                {text.appStore}
              </a>
              <a
                href="https://play.google.com/store/apps/details?id=com.ebru.whatsinapp"
                className="inline-flex items-center justify-center gap-3 border-2 text-gray-600 px-8 py-4 text-lg font-semibold rounded-xl bg-white transition-all opacity-60 cursor-not-allowed no-underline"
                style={{ backgroundColor: "white", borderColor: "black" }}
              >
                {text.playStore}
              </a>
            </div>

            <div className="public-stats flex items-center gap-8 pt-6">
              <div className="text-center">
                <div className="text-3xl font-bold" style={{ color: "#ff9f43" }}>
                  50K+
                </div>
                <div className="text-sm text-gray-600">{text.products}</div>
              </div>
              <div className="text-center">
                <div className="flex items-center gap-1 text-3xl font-bold text-gray-700 justify-center">
                  <Star className="w-8 h-8 fill-current" style={{ color: "#ff9f43" }} />
                  4.8
                </div>
                <div className="text-sm text-gray-600">{text.store}</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-pink-500">10000+</div>
                <div className="text-sm text-gray-600">{text.users}</div>
              </div>
            </div>
          </div>

          <div className="relative order-2 lg:order-2 flex justify-center mt-8 lg:mt-0">
            <div className="relative">
              <div className="public-phone relative mx-auto w-72 h-[580px] md:w-80 md:h-[650px] bg-gradient-to-br from-gray-900 to-black rounded-[3rem] p-2 shadow-2xl">
                {/* Phone Screen */}
                <div className="public-phone-screen w-full h-full rounded-[2.5rem] overflow-hidden relative bg-white">
                  {/* Status Bar */}
                  <div className="bg-gray-50 px-6 py-4 flex justify-between items-center text-xs font-medium text-gray-800">
                    <span>9:41</span>
                    <div className="w-20 h-4 bg-black rounded-full"></div>
                    <div className="flex items-center gap-1">
                      <div className="w-4 h-2 bg-green-500 rounded-sm"></div>
                      <span className="text-xs">100%</span>
                    </div>
                  </div>

                  {/* App Header */}
                  <div className="px-6 py-5" style={{ backgroundColor: "#ff9f43" }}>
                    <div className="flex items-center justify-center">
                      <span className="text-white font-bold text-lg">whatsin?</span>
                    </div>
                  </div>

                  {/* Search Bar */}
                  <div className="px-6 py-5 bg-gray-50">
                    <div className="bg-white rounded-xl px-4 py-4 shadow-sm border border-gray-200">
                      <div className="flex items-center justify-center gap-3">
                        <Search className="w-5 h-5 text-gray-400" />
                        <div className="text-gray-400 text-sm">{text.searchPlaceholder}</div>
                      </div>
                    </div>
                  </div>

                  {/* App Content */}
                  <div
                    className="public-phone-content flex flex-col items-center justify-center bg-white"
                  >
                    <div className="text-center space-y-6">
                      <div
                        className="w-24 h-24 mx-auto rounded-2xl flex items-center justify-center animate-pulse shadow-lg"
                        style={{ backgroundColor: "#fff7ed" }}
                      >
                        <Search className="w-12 h-12" style={{ color: "#ff9f43" }} />
                      </div>
                      <div className="space-y-4">
                        <div className="text-xl font-semibold text-gray-800">{text.analyzing}</div>
                        <div className="w-48 h-2 bg-gray-200 rounded-full overflow-hidden relative mx-auto">
                          <div
                            className="absolute top-0 left-0 h-full rounded-full animate-search-progress"
                            style={{ backgroundColor: "#ff9f43" }}
                          ></div>
                        </div>
                        <div className="text-sm text-gray-500">{text.checking}</div>
                      </div>
                    </div>
                  </div>

                  {/* Tab Bar */}
                  <div className="public-phone-tabs bg-white border-t border-gray-100 rounded-b-[2.5rem]">
                    <div className="flex justify-around items-center">
                      <div className="flex flex-col items-center gap-1">
                        <div
                          className="w-8 h-8 rounded-xl flex items-center justify-center"
                          style={{ backgroundColor: "#ff9f43" }}
                        >
                          <Search className="w-4 h-4 text-white" />
                        </div>
                        <div className="text-xs font-medium" style={{ color: "#ff9f43" }}>
                          {text.search}
                        </div>
                      </div>
                      <div className="flex flex-col items-center gap-1">
                        <div className="w-8 h-8 bg-gray-200 rounded-xl flex items-center justify-center">
                          <Heart className="w-4 h-4 text-gray-500" />
                        </div>
                        <div className="text-xs text-gray-400">{text.favorites}</div>
                      </div>
                      <div className="flex flex-col items-center gap-1">
                        <div className="w-8 h-8 bg-gray-200 rounded-xl flex items-center justify-center">
                          <Users className="w-4 h-4 text-gray-500" />
                        </div>
                        <div className="text-xs text-gray-400">{text.profile}</div>
                      </div>
                      <div className="flex flex-col items-center gap-1">
                        <div className="w-8 h-8 bg-gray-200 rounded-xl flex items-center justify-center">
                          <Sparkles className="w-4 h-4 text-gray-500" />
                        </div>
                        <div className="text-xs text-gray-400">{text.discover}</div>
                      </div>
                    </div>

                    {/* Home Indicator */}
                    <div className="mt-3 flex justify-center">
                      <div className="w-32 h-1 bg-gray-300 rounded-full"></div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Floating elements */}
              <div className="absolute -top-4 -right-4 w-16 h-16 bg-pink-100 rounded-full flex items-center justify-center animate-bounce shadow-lg">
                <Heart className="w-8 h-8 text-pink-500" />
              </div>
              <div
                className="absolute -bottom-4 -left-4 w-16 h-16 rounded-full flex items-center justify-center animate-pulse shadow-lg"
                style={{ backgroundColor: "#fff7ed" }}
              >
                <Shield className="w-8 h-8" style={{ color: "#ff9f43" }} />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 bg-gradient-to-br from-white via-orange-50 to-white">
        <div className="container mx-auto px-4">
          <div className="text-center space-y-6 mb-16">
            <div
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium shadow-sm"
              style={{ backgroundColor: "#fff7ed", color: "#ff9f43" }}
            >
              <Sparkles className="w-4 h-4" /> {text.featureBadge}
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-800">
              {text.why}
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              {text.featureDescription}
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-all border-0 hover:scale-105 group">
              <div className="text-center space-y-4">
                <div
                  className="w-16 h-16 mx-auto rounded-2xl flex items-center justify-center group-hover:rotate-12 transition-transform shadow-md"
                  style={{ backgroundColor: "#fff7ed" }}
                >
                  <Camera className="w-8 h-8" style={{ color: "#ff9f43" }} />
                </div>
                <h3 className="text-xl font-bold text-gray-800">{text.featureTitles[0]}</h3>
                <p className="text-gray-600">{text.featureDescriptions[0]}</p>
              </div>
            </div>

            <div className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-all border-0 hover:scale-105 group">
              <div className="text-center space-y-4">
                <div className="w-16 h-16 mx-auto bg-gradient-to-br from-blue-400 to-blue-600 rounded-2xl flex items-center justify-center group-hover:rotate-12 transition-transform shadow-md">
                  <Shield className="w-8 h-8" />
                </div>
                <h3 className="text-xl font-bold text-gray-800">{text.featureTitles[1]}</h3>
                <p className="text-gray-600">{text.featureDescriptions[1]}</p>
              </div>
            </div>

            <div className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-all border-0 hover:scale-105 group">
              <div className="text-center space-y-4">
                <div className="w-16 h-16 mx-auto bg-gradient-to-br from-pink-400 to-pink-600 rounded-2xl flex items-center justify-center group-hover:rotate-12 transition-transform shadow-md">
                  <Heart className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-800">{text.featureTitles[2]}</h3>
                <p className="text-gray-600">{text.featureDescriptions[2]}</p>
              </div>
            </div>

            <div className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-all border-0 hover:scale-105 group">
              <div className="text-center space-y-4">
                <div className="w-16 h-16 mx-auto bg-gradient-to-br from-purple-400 to-purple-600 rounded-2xl flex items-center justify-center group-hover:rotate-12 transition-transform shadow-md">
                  <Search className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-800">{text.featureTitles[3]}</h3>
                <p className="text-gray-600">{text.featureDescriptions[3]}</p>
              </div>
            </div>

            <div className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-all border-0 hover:scale-105 group">
              <div className="text-center space-y-4">
                <div className="w-16 h-16 mx-auto bg-gradient-to-br from-yellow-400 to-yellow-600 rounded-2xl flex items-center justify-center group-hover:rotate-12 transition-transform shadow-md">
                  <Zap className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-800">{text.featureTitles[4]}</h3>
                <p className="text-gray-600">{text.featureDescriptions[4]}</p>
              </div>
            </div>

            <div className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-all border-0 hover:scale-105 group">
              <div className="text-center space-y-4">
                <div className="w-16 h-16 mx-auto bg-gradient-to-br from-green-400 to-green-600 rounded-2xl flex items-center justify-center group-hover:rotate-12 transition-transform shadow-md">
                  <Sparkles className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-800">{text.featureTitles[5]}</h3>
                <p className="text-gray-600">{text.featureDescriptions[5]}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section id="how-it-works" className="py-20">
        <div className="container mx-auto px-4">
          <div className="text-center space-y-6 mb-16">
            <div
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium shadow-sm"
              style={{ backgroundColor: "#fff7ed", color: "#ff9f43" }}
            >
              <Zap className="w-4 h-4" /> {text.easy}
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-800">
              {text.stepsTitle}
            </h2>
          </div>

          <div className="grid sm:grid-cols-3 gap-8 max-w-4xl mx-auto">
            <div className="text-center space-y-6 group">
              <div className="relative">
                <div
                  className="w-24 h-24 mx-auto rounded-full flex items-center justify-center text-white text-2xl font-bold group-hover:scale-110 transition-transform shadow-lg"
                  style={{ background: "linear-gradient(135deg, #ff9f43 0%, #f7931e 100%)" }}
                >
                  1
                </div>
              </div>
              <div className="space-y-3">
                <h3 className="text-2xl font-bold text-gray-800">{text.stepTitles[0]}</h3>
                <p className="text-gray-600">
                  {text.stepDescriptions[0]}
                </p>
              </div>
            </div>

            <div className="text-center space-y-6 group">
              <div className="relative">
                <div
                  className="w-24 h-24 mx-auto rounded-full flex items-center justify-center text-white text-2xl font-bold group-hover:scale-110 transition-transform shadow-lg"
                  style={{ background: "linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)" }}
                >
                  2
                </div>
              </div>
              <div className="space-y-3">
                <h3 className="text-2xl font-bold text-gray-800">{text.stepTitles[1]}</h3>
                <p className="text-gray-600">{text.stepDescriptions[1]}</p>
              </div>
            </div>

            <div className="text-center space-y-6 group">
              <div className="relative">
                <div
                  className="w-24 h-24 mx-auto rounded-full flex items-center justify-center text-white text-2xl font-bold group-hover:scale-110 transition-transform shadow-lg"
                  style={{ background: "linear-gradient(135deg, #4ade80 0%, #22c55e 100%)" }}
                >
                  3
                </div>
              </div>
              <div className="space-y-3">
                <h3 className="text-2xl font-bold text-gray-800">{text.stepTitles[2]}</h3>
                <p className="text-gray-600">{text.stepDescriptions[2]}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section
        className="py-20"
        style={{ background: "linear-gradient(135deg, #ff9f43 0%, #f7931e 50%, #e8873a 100%)" }}
      >
        <div className="container mx-auto px-4 text-center">
          <div className="max-w-3xl mx-auto space-y-8 text-white">
            <h2 className="text-4xl md:text-5xl font-bold leading-tight">{text.ctaTitle}</h2>
            <p className="text-xl opacity-90">
              {text.ctaDescription}
            </p>
            <div className="public-store-actions flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="https://apps.apple.com/app/id6744443804"
                className="inline-flex items-center justify-center gap-3 bg-white hover:bg-gray-100 px-8 py-4 text-lg font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all no-underline"
                style={{ color: "#ff9f43" }}
              >
                <Smartphone className="w-5 h-5" />
                {text.appStore}
              </a>
              <a
                href="https://play.google.com/store/apps/details?id=com.ebru.whatsinapp"
                className="inline-flex items-center justify-center gap-3 border-2 text-gray-600 px-8 py-4 text-lg font-semibold rounded-xl bg-white transition-all opacity-60 cursor-not-allowed no-underline"
                style={{ backgroundColor: "white", borderColor: "black" }}
              >
                {text.playStore}
              </a>
            </div>
            <div className="flex flex-wrap items-center justify-center gap-6 pt-6 text-sm opacity-80">
              <div className="flex items-center gap-2">
                <Star className="w-5 h-5 fill-current" />
                <span>4.8/5 {text.store}</span>
              </div>
              <div className="flex items-center gap-2">
                <Users className="w-5 h-5" />
                <span>{text.happyUsers}</span>
              </div>
              <div className="flex items-center gap-2">
                <Shield className="w-5 h-5" />
                <span>{text.analysis}</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      </main>
      <PublicFooter />
    </div>
  )
}

export default HomePage
